import { useState, useEffect, useCallback } from 'react'
import { Drawer, IconButton, Switch, FormControlLabel, Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { default as SettingsIcon } from '@mui/icons-material/Settings'
import { default as CloseIcon } from '@mui/icons-material/Close'
import PrintIcon from '@mui/icons-material/Print'
import PrintDisabledIcon from '@mui/icons-material/PrintDisabled'
import styled from 'styled-components'
import { HARD_CODED_SCREENING_EMR_SECTION_IDS } from '../util'

// Define the sections that appear in the screening report form and print
// These match what the user sees on the form
const SCREENING_REPORT_SECTIONS = [
  { id: 'vitals', label: 'Vitals', sectionCode: 'vitals' },
  { id: 'screening-questions', label: 'Screening Questions', sectionCode: 'screening-questions' },
  { id: 'presenting-complaint', label: 'Presenting Complaint', sectionCode: 'presenting-complaint' },
  { id: 'observations', label: 'Observations', sectionCode: 'observations' },
  { id: 'primary-clinical-diagnosis', label: 'Primary Clinical Diagnosis', sectionCode: 'primary-clinical-diagnosis' },
  { id: 'differential-clinical-diagnoses', label: 'Other Clinical Diagnoses', sectionCode: 'differential-clinical-diagnoses' },
  { id: 'diagnosis', label: 'Diagnosis', sectionCode: 'diagnosis' },
  { id: 'advice', label: 'Advice', sectionCode: 'advice' },
  { id: 'screening-report-notes', label: 'Screening Report Notes', sectionCode: 'screening-report-notes' },
  { id: 'patient-history', label: 'Patient History', sectionCode: 'PATIENT_HISTORY' }, // EMR section
  { id: 'private-notes', label: 'Private Notes', sectionCode: 'private-notes' },
]

const STORAGE_KEY = 'screening-report-emr-settings'

const DrawerContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: linear-gradient(135deg, rgba(250, 250, 255, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%);
`

const DrawerHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.98) 0%, rgba(99, 102, 241, 0.98) 100%);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 4px 16px rgba(67, 56, 202, 0.25), 0 1px 3px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
  min-height: 72px;
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    min-height: 64px;
  }
`

const DrawerTitle = styled(Typography)`
  font-size: 20px;
  font-weight: 700;
  color: white;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  align-items: center;
  gap: 10px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  letter-spacing: -0.02em;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 17px;
    gap: 8px;
  }
`

const DrawerContent = styled(Box)`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px 12px;
  }
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 12px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(67, 56, 202, 0.08);
    border-radius: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(67, 56, 202, 0.3);
    border-radius: 6px;
    border: 2px solid rgba(67, 56, 202, 0.08);
    
    &:hover {
      background: rgba(67, 56, 202, 0.5);
    }
  }
  
  /* Firefox scrollbar */
  scrollbar-width: thin;
  scrollbar-color: rgba(67, 56, 202, 0.3) rgba(67, 56, 202, 0.08);
`

const SectionItem = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  margin-bottom: 12px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-color: rgba(67, 56, 202, 0.3);
  }
`

const SectionInfo = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const SectionName = styled(Typography)`
  font-size: 15px;
  font-weight: 600;
  color: #333;
`

const SectionDescription = styled(Typography)`
  font-size: 13px;
  color: #666;
`

const EmptyState = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  color: #999;
`

const ScreeningReportEmrSettings = ({ emrSections = [], onSettingsChange, onOpen }) => {
  const [open, setOpen] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  
  // State to track printable status for each EMR section
  const [sectionPrintSettings, setSectionPrintSettings] = useState({})

  // Expose open function to parent via onOpen callback
  useEffect(() => {
    if (onOpen) {
      onOpen(() => setOpen(true))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        const parsed = JSON.parse(saved)
        // Ensure all sections are in the settings
        const completeSettings = { ...parsed }
        SCREENING_REPORT_SECTIONS.forEach((section) => {
          if (completeSettings[section.sectionCode] === undefined) {
            completeSettings[section.sectionCode] = true
          }
        })
        setSectionPrintSettings(completeSettings)
      } else {
        // Initialize all sections as printable by default
        const defaultSettings = {}
        SCREENING_REPORT_SECTIONS.forEach((section) => {
          defaultSettings[section.sectionCode] = true // Default to printable
        })
        setSectionPrintSettings(defaultSettings)
      }
    } catch (error) {
      console.error('Error loading EMR section settings:', error)
      // On error, use defaults
      const defaultSettings = {}
      SCREENING_REPORT_SECTIONS.forEach((section) => {
        defaultSettings[section.sectionCode] = true
      })
      setSectionPrintSettings(defaultSettings)
    }
  }, [])

  // Notify parent when settings change (after render)
  useEffect(() => {
    if (onSettingsChange && Object.keys(sectionPrintSettings).length > 0) {
      onSettingsChange(sectionPrintSettings)
    }
  }, [sectionPrintSettings, onSettingsChange])

  const togglePrintable = useCallback((sectionId) => {
    setSectionPrintSettings((prev) => {
      const updated = {
        ...prev,
        [sectionId]: !prev[sectionId],
      }
      
      // Save to localStorage
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
      } catch (error) {
        console.error('Error saving EMR section settings:', error)
      }
      
      return updated
    })
  }, [])

  // Use the hardcoded list of sections that match what the user sees on the form
  // These are the sections that appear in the screening report form and print
  const sectionsToShow = SCREENING_REPORT_SECTIONS

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 480,
            maxWidth: '100vw',
          },
        }}
      >
        <DrawerContainer>
          <DrawerHeader>
            <DrawerTitle>
              <SettingsIcon sx={{ fontSize: 24 }} />
              EMR Sections Print Settings
            </DrawerTitle>
            <IconButton
              onClick={() => setOpen(false)}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DrawerHeader>
          
          <DrawerContent>
            {sectionsToShow.length === 0 ? (
              <EmptyState>
                <Typography variant="body1" sx={{ color: '#999', mb: 1 }}>
                  No EMR sections available
                </Typography>
                <Typography variant="body2" sx={{ color: '#bbb' }}>
                  EMR sections will appear here once they are configured
                </Typography>
              </EmptyState>
            ) : (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#666',
                    mb: 3,
                    fontSize: '14px',
                    lineHeight: 1.6,
                  }}
                >
                  Control which EMR sections are included in the report preview and print. 
                  Toggle sections on/off to customize what appears in the printed report.
                </Typography>
                
                {sectionsToShow.map((section) => {
                  // Use sectionCode as the key for settings
                  const sectionCode = section.sectionCode
                  const isPrintable = sectionPrintSettings[sectionCode] !== false
                  
                  // Use the label from the section definition
                  const sectionName = section.label
                  
                  return (
                    <SectionItem key={sectionCode}>
                      <SectionInfo>
                        <SectionName>{sectionName}</SectionName>
                        {section.description && (
                          <SectionDescription>{section.description}</SectionDescription>
                        )}
                      </SectionInfo>
                      
                      <Box display="flex" alignItems="center" gap={1}>
                        {isPrintable ? (
                          <PrintIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                        ) : (
                          <PrintDisabledIcon sx={{ color: '#999', fontSize: 20 }} />
                        )}
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isPrintable}
                              onChange={() => togglePrintable(sectionCode)}
                              size="small"
                            />
                          }
                          label=""
                          sx={{ margin: 0 }}
                        />
                      </Box>
                    </SectionItem>
                  )
                })}
              </>
            )}
          </DrawerContent>
        </DrawerContainer>
      </Drawer>
    </>
  )
}

export default ScreeningReportEmrSettings

